// ── session-invites-modal: send invites confirm (Wave 6, 202 honest pending) ─
// POST /sessions/:id/send-invites — an ASYNC lane that returns 202 with an
// evidence record; the Zoom/calendar invite delivery is GATED-DARK, so a send
// records the request honestly as PENDING and NEVER claims a delivered invite or
// a created meeting. The page owns the POST + the toast (only after the 202
// confirms the request was recorded). The honest "pending — delivery is closed"
// copy keeps the dark lane truthful.

import { Alert, Button, Modal } from "@vjal/ui";

import type { CoachingSessionDto, InviteOperationRequest } from "../../services/coaching-dtos";
import { describeSessionWriteError } from "./sessions-helpers";

export interface SessionInvitesModalProps {
  open: boolean;
  session: CoachingSessionDto | null;
  onClose: () => void;
  onConfirm: (body: InviteOperationRequest) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function SessionInvitesModal({
  open,
  session,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: SessionInvitesModalProps) {
  const topError = describeSessionWriteError(submitError);
  const title = session?.title?.trim() ? session.title : "this session";

  return (
    <Modal open={open} onClose={onClose} title="Send invites">
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}
        <Alert variant="info">
          This records an invite request for {title}. Calendar and Zoom delivery is not configured in this environment,
          so invites are queued as <span className="font-semibold">pending</span> — no meeting or join link is created.
        </Alert>
        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            isLoading={submitting}
            onClick={() => {
              void onConfirm({}).catch(() => {
                // The owning mutation surfaces the error via submitError.
              });
            }}
          >
            Send invites
          </Button>
        </div>
      </div>
    </Modal>
  );
}
